/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppHeaderLogo } from '../../helpers/applicationCustomization/ui';
import HotKeys from '../../helpers/hotKeys/hotKeys';
import { fireAndForget } from '../../helpers/jsHelpers/promise';
import Localizer from '../../helpers/localization';
import { AvenueType, type FocusedAvenue, type TitledAvenue } from '../../helpers/ui/avenue/types';
import { RegistrationType, type UserInfo } from '../../redux/appRegistration/types/types';
import { getSettingValueByName } from '../../redux/netgemApi/actions/helpers/settings';
import { getImageUrl } from '../../redux/netgemApi/actions/v8/metadataImage';
import { navigateToAvenue } from '../../redux/ui/actions';
import NavigationMenu from '../navigationMenu/NavigationMenu';
import SearchBox, { type SearchBoxHandle } from '../search/SearchBox';
import './Header.css';
import FreeTrialButton from './FreeTrialButton';
import { HEADER_LOGO_HEIGHT, HEADER_LOGO_WIDTH, type HeaderPropType, LOGO_LOAD_TIMEOUT, type SecurityInfoType } from './HeaderConstsAndTypes';
import { buildSecurityInfo, getHeaderClass, logSecurityInfo } from './HeaderHelper';
import SubscriptionButton from './SubscriptionButton';
import UserMenu from './UserMenu';

const Header = ({ isScrolling, onLogoLoaded, onSearch }: HeaderPropType): React.Node => {
  const dispatch = useDispatch();

  const [isLogoLoaded, setIsLogoLoaded] = useState<boolean>(false);
  const [headerClass, setHeaderClass] = useState<string | null>(null);
  const [distributorLogo, setDistributorLogo] = useState<string | null>(null);

  const applicationName = useSelector<string>((state) => state.appConfiguration.applicationName);
  const focusedAvenue = useSelector<FocusedAvenue>((state) => state.ui.navigationStack[state.ui.navigationStack - 1]);
  const mainAvenue = useSelector<TitledAvenue | null>((state) => state.ui.mainAvenue);
  const isDebugModeEnabled = useSelector<boolean>((state) => state.appConfiguration.isDebugModeEnabled);
  const isInTheaterMode = useSelector<boolean>((state) => state.ui.isInTheaterMode);
  const securityInfo = useSelector<SecurityInfoType>((state) => buildSecurityInfo(state));
  const subscriptionButton = useSelector<boolean>((state) => state.appConfiguration.subscriptionButton);
  const hasFreeTrialButton = useSelector<boolean>((state) => state.appConfiguration.hasFreeTrialButton);
  const freeTrialUrl = useSelector<string>((state) => state.appConfiguration.configuration.subscribeWebUrl ?? '');
  const isRegisteredAsGuest = useSelector<boolean>((state) => state.appRegistration.registration === RegistrationType.RegisteredAsGuest);
  const userInfo = useSelector<UserInfo | null>((state) => state.appRegistration.userInfo);

  // Check for distributor logo which, if any, overrides regular logo
  const distributorLogoId = useSelector<string | null>((state) => getSettingValueByName('app.toolbar', 'logoid', state.appConfiguration));

  /*
   * Used to display "Les offres" link but not in use for the moment
   * const subscriptionPresentationDisplay = useSelector((state) => state.appConfiguration.subscriptionPresentationDisplay);
   * const subscriptionPresentationUrl = useSelector((state) => state.appConfiguration.configuration.subscriptionPresentationUrl ?? '');
   */

  const logoLoadTimer = useRef<TimeoutID | null>(null);
  const searchBoxRef = useRef<SearchBoxHandle | null>(null);

  useEffect(() => {
    const loadLogo = async (assetId: string) => {
      const logoUrl = await dispatch(getImageUrl({ assetId, height: HEADER_LOGO_HEIGHT, width: HEADER_LOGO_WIDTH }));
      setDistributorLogo(logoUrl);
    };

    if (distributorLogoId === null) {
      return;
    }

    fireAndForget(loadLogo(distributorLogoId), 'Header.loadLogo');
  }, [distributorLogoId]);

  const resetLogoLoadTimer = useCallback(() => {
    if (logoLoadTimer.current) {
      clearTimeout(logoLoadTimer.current);
      logoLoadTimer.current = null;
    }
  }, []);

  const handleHeaderImageOnLoad = useCallback(() => {
    resetLogoLoadTimer();
    setIsLogoLoaded(true);
    onLogoLoaded();
  }, [onLogoLoaded, resetLogoLoadTimer]);

  useEffect(() => {
    const handleOpenSearchBoxHotKey = (event: SyntheticKeyboardEvent<HTMLElement>) => {
      if ((!focusedAvenue || focusedAvenue.type !== AvenueType.Search) && searchBoxRef.current && searchBoxRef.current.isClosed()) {
        event.preventDefault();
        event.stopPropagation();
        searchBoxRef.current?.open();
      }
    };

    HotKeys.register('s', handleOpenSearchBoxHotKey, { name: 'Header.openSearch' });
    logoLoadTimer.current = setTimeout(handleHeaderImageOnLoad, LOGO_LOAD_TIMEOUT);

    return () => {
      HotKeys.unregister('s', handleOpenSearchBoxHotKey);
      resetLogoLoadTimer();
    };
  }, [focusedAvenue, handleHeaderImageOnLoad, resetLogoLoadTimer, searchBoxRef.current]);

  useEffect(() => {
    setHeaderClass(getHeaderClass(isInTheaterMode, isLogoLoaded, isScrolling));
  }, [isInTheaterMode, isLogoLoaded, isScrolling]);

  const handleHeaderImageOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      if (isDebugModeEnabled && (event.ctrlKey || event.altKey) && event.shiftKey) {
        logSecurityInfo(securityInfo);
        return;
      }

      dispatch(navigateToAvenue(mainAvenue.index, mainAvenue.type));
    },
    [isDebugModeEnabled, mainAvenue, securityInfo],
  );

  return (
    <header className={headerClass} inert={isInTheaterMode}>
      <div className='headerLeft'>
        <img
          alt={Localizer.localize('accessibility.navigation.application_logo', { applicationName })}
          className='headerImage'
          draggable={false}
          onClick={handleHeaderImageOnClick}
          onLoad={handleHeaderImageOnLoad}
          src={distributorLogo ?? AppHeaderLogo}
        />
      </div>
      <div className='headerMiddle'>
        <NavigationMenu />
      </div>
      <div className='headerRight'>
        {/*<SubscriptionPresentationButton dispatch={dispatch} displayType={subscriptionPresentationDisplay} url={subscriptionPresentationUrl} />*/}
        <SubscriptionButton isButtonDisplayed={subscriptionButton} isRegisteredAsGuest={isRegisteredAsGuest} userInfo={userInfo} />
        <SearchBox onSearch={onSearch} ref={searchBoxRef} />
        <FreeTrialButton hasButton={hasFreeTrialButton} isRegisteredAsGuest={isRegisteredAsGuest} url={freeTrialUrl} />
        <UserMenu />
      </div>
    </header>
  );
};

export default Header;
